import { Drawer, List } from '@mui/material';
import { useLocation } from '@tanstack/react-router';
import { useMatchRoute } from '@tanstack/react-router';
import { useRouterState } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import type { FC, ElementType } from 'react';
import { getRoutes } from 'src/constants/routes';

import { NavbarMenuItem } from '../molecules/navbar-menu-item';

interface NavbarMenuProps {
  onClose: () => void;
  open: boolean;
}

interface Item {
  href?: string;
  external?: boolean;
  icon: ElementType;
  items?: Array<{ href: string; title: string }>;
  title: string;
}

export const NavbarMenu: FC<NavbarMenuProps> = props => {
  const { open, onClose } = props;
  const pathname = useLocation({ select: location => location.pathname });
  const [openedItem, setOpenedItem] = useState<Item | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(null);
  const [activeHref, setActiveHref] = useState('');

  const matchRoute = useMatchRoute();
  const routerState = useRouterState();

  const routes = useMemo(() => getRoutes(), []);

  const handleOpenItem = (item: Item): void => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };

  useEffect(() => {
    routes.forEach(route => {
      if (route.subRoutes) {
        for (let index = 0; index < route.subRoutes.length; index++) {
          const matchedRoute = matchRoute({ to: route.subRoutes[index].href });

          const isActive =
            (typeof matchedRoute === 'object' &&
              !Array.isArray(matchedRoute) &&
              matchedRoute !== null) ||
            !!matchedRoute;

          if (isActive) {
            setActiveItem(route);
            setActiveHref(route.subRoutes[index].href);
            setOpenedItem(route);
            break;
          }
        }
      } else {
        const matchedRoute = matchRoute({ to: route.href, fuzzy: true });

        const isActive =
          (typeof matchedRoute === 'object' &&
            !Array.isArray(matchedRoute) &&
            matchedRoute !== null) ||
          !!matchedRoute;

        if (isActive) {
          setActiveItem(route);
          setOpenedItem(route);
        }
      }
    });
  }, [pathname, routerState, matchRoute]);

  return (
    <Drawer
      anchor="top"
      onClose={onClose}
      open={open}
      transitionDuration={0}
      ModalProps={{
        BackdropProps: {
          invisible: true,
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: '#2B2F3C',
          color: '#B2B7C8',
          display: 'flex',
          flexDirection: 'column',
          top: 64,
          maxHeight: 'calc(100% - 64px)',
          width: '100vw',
        },
      }}>
      <List>
        {activeItem &&
          routes.map(item => (
            <NavbarMenuItem
              active={activeItem?.title === item.title}
              activeHref={activeHref}
              key={item.title}
              onClose={onClose}
              onOpenItem={() => handleOpenItem(item)}
              open={openedItem?.title === item.title}
              {...item}
            />
          ))}
      </List>
    </Drawer>
  );
};
